import languageData from '../../data/language.json';
import { useState, useEffect } from 'react';

const Home = () => {
  const pokeInfoWeb = 'https://pokeinfo-website.vercel.app';
  const pokeInfoGit = 'https://github.com/Patrik848/PokeInfo-website';
  const stockPredGit = 'https://github.com/Patrik848/Stock-Prediction';
  const zunoWeb = 'https://zuno-iota.vercel.app';
  const zunoGit = 'https://github.com/Patrik-Corri/Zuno';

  const [activeSection, setActiveSection] = useState('about');
  const [lightTheme, setLightTheme] = useState(false);
  const [iconChanging, setIconChanging] = useState(false);

  const L = lightTheme;

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['about', 'skills', 'projects'];
      let current = '';

      sections.forEach((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            current = section;
          }
        }
      });

      if (current) {
        setActiveSection(current);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLightMode = () => {
    setIconChanging(true);
    setTimeout(() => {
      setLightTheme((prev) => !prev);
      setIconChanging(false);
    }, 200);
  };

  const navLink = (id, label) => (
    <a
      href={`#${id}`}
      className={`relative px-1 py-2 text-xs font-medium transition-colors sm:text-sm ${
        activeSection === id
          ? L
            ? 'text-violet-700'
            : 'text-violet-300'
          : L
            ? 'text-zinc-600 hover:text-zinc-900'
            : 'text-zinc-400 hover:text-zinc-200'
      }`}
    >
      {label}
      <span
        className={`absolute bottom-0 left-0 h-0.5 rounded-full transition-all duration-300 ${
          activeSection === id ? 'w-full' : 'w-0'
        } ${L ? 'bg-violet-600' : 'bg-violet-400'}`}
        aria-hidden
      />
    </a>
  );

  const sectionTitle = (text) => (
    <h2
      className={`font-display mb-3 text-center text-3xl font-semibold tracking-tight sm:text-4xl ${
        L ? 'text-zinc-900' : 'text-white'
      }`}
    >
      {text}
    </h2>
  );

  const sectionSubtitle = (text) => (
    <p
      className={`mx-auto mb-12 max-w-xl text-center text-sm sm:text-base ${
        L ? 'text-zinc-600' : 'text-zinc-400'
      }`}
    >
      {text}
    </p>
  );

  return (
    <div
      className={`min-h-screen font-sans transition-colors duration-300 ${
        L ? 'bg-zinc-50 text-zinc-900' : 'bg-zinc-950 text-zinc-100'
      }`}
    >
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors ${
          L
            ? 'border-zinc-200/80 bg-white/75'
            : 'border-white/5 bg-zinc-950/75'
        }`}
      >
        <nav className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-5 py-3 sm:gap-4 sm:px-8">
          <a
            href="#about"
            className={`font-display shrink-0 text-base font-semibold tracking-tight sm:text-lg ${
              L ? 'text-zinc-900' : 'text-white'
            }`}
          >
            Patrik Corri
          </a>
          <div className="flex flex-wrap items-center justify-end gap-3 sm:gap-6 md:gap-8">
            {navLink('about', 'About')}
            {navLink('skills', 'Skills')}
            {navLink('projects', 'Projects')}
            <button
              type="button"
              onClick={toggleLightMode}
              className={`rounded-lg p-2 transition-colors ${
                L
                  ? 'border border-zinc-300 bg-zinc-200/90 shadow-sm hover:bg-zinc-300'
                  : 'text-violet-300 hover:bg-white/5'
              }`}
              aria-label={L ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              <img
                src={L ? 'assets/sun.png' : 'assets/moon.png'}
                alt=""
                width={22}
                height={22}
                className={`transition-all duration-200 ${
                  L ? 'brightness-0 contrast-110' : ''
                } ${iconChanging ? 'scale-75 rotate-90 opacity-0' : 'opacity-100'}`}
              />
            </button>
          </div>
        </nav>
      </header>

      <main>
        <section
          id="about"
          className={`relative flex min-h-screen flex-col justify-center overflow-hidden px-5 py-20 pt-28 sm:px-8 sm:pt-32 ${
            L ? 'bg-zinc-50' : 'bg-zinc-950'
          }`}
        >
          {!L && (
            <>
              <div
                className="pointer-events-none absolute -left-32 top-20 h-80 w-80 rounded-full bg-violet-600/25 blur-[100px]"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute -right-20 bottom-10 h-72 w-72 rounded-full bg-cyan-500/15 blur-[90px]"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute left-1/2 top-1/2 h-px w-[min(90vw,48rem)] -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-transparent via-violet-500/30 to-transparent"
                aria-hidden
              />
            </>
          )}
          {L && (
            <div
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(139,92,246,0.12),transparent)]"
              aria-hidden
            />
          )}

          <div className="relative mx-auto flex max-w-3xl flex-col items-center text-center">
            <h1
              className={`font-display mb-4 text-4xl font-bold leading-tight tracking-tight sm:text-5xl md:text-6xl ${
                L ? 'text-zinc-900' : 'text-white'
              }`}
            >
              Hey, I&apos;m{' '}
              <span className={L ? 'text-violet-700' : 'bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent'}>
                Patrik
              </span>
            </h1>
            <p className={`mb-2 text-sm sm:text-base ${L ? 'text-zinc-600' : 'text-zinc-400'}`}>
              Computer science student · Full-stack developer
            </p>
            <p className={`mb-8 text-sm ${L ? 'text-zinc-500' : 'text-zinc-500'}`}>
              Jacksonville, FL
            </p>
            <p
              className={`mx-auto mb-10 max-w-xl text-base leading-relaxed sm:text-lg ${
                L ? 'text-zinc-600' : 'text-zinc-300'
              }`}
            >
              I build full-stack web applications and keep leveling up with new tools and frameworks.
              I enjoy shipping projects that show how I think, learn, and write code.
            </p>

            <div className="mb-8 flex flex-wrap items-center justify-center gap-3">
              <a
                href="mailto:patrikcorri84@gmail.com"
                className={`flex h-12 w-12 items-center justify-center rounded-xl border transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  L
                    ? 'border-zinc-200 bg-white text-zinc-700 hover:border-violet-300'
                    : 'border-white/10 bg-white/5 hover:border-violet-500/40 hover:bg-white/10'
                }`}
                aria-label="Email"
              >
                <img
                  src={L ? 'assets/dark-email.png' : 'assets/email.png'}
                  alt=""
                  className="h-7 w-7 object-contain"
                />
              </a>
              <a
                href="https://www.linkedin.com/in/patrik-corri-6173b72b6/"
                className={`flex h-12 w-12 items-center justify-center rounded-xl border transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  L
                    ? 'border-zinc-200 bg-white hover:border-violet-300'
                    : 'border-white/10 bg-white/5 hover:border-violet-500/40 hover:bg-white/10'
                }`}
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
              >
                <img
                  src={L ? 'assets/dark-linkedin.png' : 'assets/linkedin.png'}
                  alt=""
                  className="h-7 w-7 object-contain"
                />
              </a>
              <a
                href="https://github.com/Patrik-Corri"
                className={`flex h-12 w-12 items-center justify-center rounded-xl border transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  L
                    ? 'border-zinc-200 bg-white hover:border-violet-300'
                    : 'border-white/10 bg-white/5 hover:border-violet-500/40 hover:bg-white/10'
                }`}
                target="_blank"
                rel="noreferrer"
                aria-label="GitHub"
              >
                <img
                  src={L ? 'assets/dark-github.png' : 'assets/github.png'}
                  alt=""
                  className="h-7 w-7 object-contain"
                />
              </a>
            </div>

            <a
              href="/assets/Resume.pdf"
              download
              className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl ${
                L
                  ? 'bg-zinc-900 text-white hover:bg-zinc-800'
                  : 'bg-gradient-to-r from-violet-600 to-violet-500 text-white hover:from-violet-500 hover:to-violet-400'
              }`}
            >
              <img
                src="assets/filedownload.png"
                alt=""
                className={`h-5 w-5 ${L ? '' : 'brightness-0 invert'}`}
              />
              Download resume
            </a>
          </div>
        </section>

        <section
          id="skills"
          className={`flex min-h-screen flex-col justify-center px-5 py-24 sm:px-8 ${L ? 'bg-white' : 'bg-zinc-900/50'}`}
        >
          <div className="mx-auto w-full max-w-5xl">
            {sectionTitle('Skills & tools')}
            {sectionSubtitle('Languages, frameworks, and platforms I use to build and ship software.')}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {languageData.languages.map((lang, index) => (
                <div
                  key={index}
                  className={`group flex items-center gap-4 rounded-2xl border p-5 transition-all hover:-translate-y-0.5 ${
                    L
                      ? 'border-zinc-200 bg-zinc-50/80 hover:border-violet-200 hover:shadow-md'
                      : 'border-white/5 bg-zinc-950/80 hover:border-violet-500/25 hover:shadow-lg hover:shadow-violet-950/20'
                  }`}
                >
                  <div
                    className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${
                      L ? 'bg-white shadow-sm' : 'bg-white/5'
                    }`}
                  >
                    <img
                      className="max-h-9 max-w-9 object-contain"
                      src={lang.cover.startsWith('/') ? lang.cover : `/${lang.cover}`}
                      alt=""
                    />
                  </div>
                  <span
                    className={`text-lg font-medium ${L ? 'text-zinc-900' : 'text-zinc-100'}`}
                  >
                    {lang.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="projects"
          className={`min-h-screen px-5 py-24 sm:px-8 ${L ? 'bg-zinc-50' : 'bg-zinc-950'}`}
        >
          <div className="mx-auto max-w-5xl">
            {sectionTitle('Selected projects')}
            {sectionSubtitle('A few builds that represent my full-stack and ML work.')}

            <div className="flex flex-col gap-10">
              <article
                className={`overflow-hidden rounded-2xl border transition-shadow hover:shadow-xl ${
                  L
                    ? 'border-zinc-200 bg-white shadow-sm'
                    : 'border-white/10 bg-zinc-900/40 shadow-lg shadow-black/20'
                }`}
              >
                <div className="grid gap-0 md:grid-cols-2">
                  <div className={`relative aspect-video md:aspect-auto md:min-h-[240px] ${L ? 'bg-zinc-100' : 'bg-zinc-800/50'}`}>
                    <img
                      src="assets/zuno.jpg"
                      alt="Zuno marketplace screenshot"
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="flex flex-col justify-center p-6 sm:p-8">
                    <h3 className={`font-display mb-3 text-xl font-semibold sm:text-2xl ${L ? 'text-zinc-900' : 'text-white'}`}>
                      Zuno — University marketplace
                    </h3>
                    <p className={`mb-6 text-sm leading-relaxed sm:text-base ${L ? 'text-zinc-600' : 'text-zinc-400'}`}>
                      Full-stack marketplace for UNF students to list, browse, and contact sellers—with flexible online or in-person payments.
                    </p>
                    <p className={`mb-3 text-xs font-semibold uppercase tracking-wide ${L ? 'text-zinc-500' : 'text-zinc-500'}`}>
                      Stack
                    </p>
                    <div className="mb-6 flex flex-wrap gap-2">
                      {['React', 'Next.js', 'Java', 'JavaScript', 'PostgreSQL'].map((tag) => (
                        <span
                          key={tag}
                          className={`rounded-lg px-2.5 py-1 text-xs font-medium ${
                            L ? 'bg-violet-100 text-violet-900' : 'bg-violet-500/15 text-violet-200'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-auto flex flex-wrap gap-3">
                      <a
                        href={zunoWeb}
                        className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                          L
                            ? 'bg-zinc-900 text-white hover:bg-zinc-800'
                            : 'bg-violet-600 text-white hover:bg-violet-500'
                        }`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Live demo
                        <img className="h-4 w-4 brightness-0 invert" src="/assets/icons8-live-24.png" alt="" />
                      </a>
                      <a
                        href={zunoGit}
                        className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
                          L
                            ? 'border-zinc-300 text-zinc-800 hover:bg-zinc-100'
                            : 'border-white/15 text-zinc-200 hover:bg-white/5'
                        }`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        GitHub
                        <img
                          className={`h-4 w-4 ${L ? '' : 'opacity-80'}`}
                          src={L ? 'assets/dark-github.png' : 'assets/github.png'}
                          alt=""
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </article>

              <article
                className={`overflow-hidden rounded-2xl border transition-shadow hover:shadow-xl ${
                  L
                    ? 'border-zinc-200 bg-white shadow-sm'
                    : 'border-white/10 bg-zinc-900/40 shadow-lg shadow-black/20'
                }`}
              >
                <div className="grid gap-0 md:grid-cols-2">
                  <div className={`relative aspect-video md:order-2 md:aspect-auto md:min-h-[240px] ${L ? 'bg-zinc-100' : 'bg-zinc-800/50'}`}>
                    <img
                      src="assets/msft.jpg"
                      alt="MSFT stock project screenshot"
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="flex flex-col justify-center p-6 sm:p-8 md:order-1">
                    <h3 className={`font-display mb-3 text-xl font-semibold sm:text-2xl ${L ? 'text-zinc-900' : 'text-white'}`}>
                      MSFT Stock — Price exploration
                    </h3>
                    <p className={`mb-6 text-sm leading-relaxed sm:text-base ${L ? 'text-zinc-600' : 'text-zinc-400'}`}>
                      Machine learning workflow to explore Microsoft stock data with pandas, matplotlib, and scikit-learn.
                    </p>
                    <p className={`mb-3 text-xs font-semibold uppercase tracking-wide ${L ? 'text-zinc-500' : 'text-zinc-500'}`}>
                      Stack
                    </p>
                    <div className="mb-6 flex flex-wrap gap-2">
                      <span
                        className={`rounded-lg px-2.5 py-1 text-xs font-medium ${
                          L ? 'bg-emerald-100 text-emerald-900' : 'bg-emerald-500/15 text-emerald-200'
                        }`}
                      >
                        Python
                      </span>
                    </div>
                    <div className="mt-auto">
                      <a
                        href={stockPredGit}
                        className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
                          L
                            ? 'border-zinc-300 text-zinc-800 hover:bg-zinc-100'
                            : 'border-white/15 text-zinc-200 hover:bg-white/5'
                        }`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View on GitHub
                        <img
                          className={`h-4 w-4 ${L ? '' : 'opacity-80'}`}
                          src={L ? 'assets/dark-github.png' : 'assets/github.png'}
                          alt=""
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </article>

              <article
                className={`overflow-hidden rounded-2xl border transition-shadow hover:shadow-xl ${
                  L
                    ? 'border-zinc-200 bg-white shadow-sm'
                    : 'border-white/10 bg-zinc-900/40 shadow-lg shadow-black/20'
                }`}
              >
                <div className="grid gap-0 md:grid-cols-2">
                  <div className={`relative aspect-video md:aspect-auto md:min-h-[240px] ${L ? 'bg-zinc-100' : 'bg-zinc-800/50'}`}>
                    <img
                      src="assets/pokeinfo.jpg"
                      alt="PokeInfo application screenshot"
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="flex flex-col justify-center p-6 sm:p-8">
                    <h3 className={`font-display mb-3 text-xl font-semibold sm:text-2xl ${L ? 'text-zinc-900' : 'text-white'}`}>
                      PokeInfo — Pokémon database
                    </h3>
                    <p className={`mb-6 text-sm leading-relaxed sm:text-base ${L ? 'text-zinc-600' : 'text-zinc-400'}`}>
                      Full-stack app to search and filter Pokémon by name, generation, type, and color, with rich detail for each entry.
                    </p>
                    <p className={`mb-3 text-xs font-semibold uppercase tracking-wide ${L ? 'text-zinc-500' : 'text-zinc-500'}`}>
                      Stack
                    </p>
                    <div className="mb-6 flex flex-wrap gap-2">
                      {['React', 'Next.js', 'Java', 'JavaScript', 'PostgreSQL'].map((tag) => (
                        <span
                          key={tag}
                          className={`rounded-lg px-2.5 py-1 text-xs font-medium ${
                            L ? 'bg-violet-100 text-violet-900' : 'bg-violet-500/15 text-violet-200'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-auto flex flex-wrap gap-3">
                      <a
                        href={pokeInfoWeb}
                        className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                          L
                            ? 'bg-zinc-900 text-white hover:bg-zinc-800'
                            : 'bg-violet-600 text-white hover:bg-violet-500'
                        }`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Live demo
                        <img className="h-4 w-4 brightness-0 invert" src="/assets/icons8-live-24.png" alt="" />
                      </a>
                      <a
                        href={pokeInfoGit}
                        className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
                          L
                            ? 'border-zinc-300 text-zinc-800 hover:bg-zinc-100'
                            : 'border-white/15 text-zinc-200 hover:bg-white/5'
                        }`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        GitHub
                        <img
                          className={`h-4 w-4 ${L ? '' : 'opacity-80'}`}
                          src={L ? 'assets/dark-github.png' : 'assets/github.png'}
                          alt=""
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <footer
          className={`border-t px-5 py-10 text-center text-sm ${
            L ? 'border-zinc-200 bg-white text-zinc-500' : 'border-white/5 bg-zinc-950 text-zinc-500'
          }`}
        >
          <p>© {new Date().getFullYear()} Patrik Corri. Built with React & Tailwind CSS.</p>
        </footer>
      </main>
    </div>
  );
};

export default Home;
